import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import useToggle from '../hooks/useToggle';
import FeatureFeedbackService from '../shared-services/Feedback/FeatureFeedbackService';

interface IFeedbackDialog {
  open: boolean;
  close: () => void;
}

enum Step {
  FeedbackStep,
  FeedbackConfimration,
}

function getPathName(path: string) {
  switch (true) {
    case new RegExp('/management/users/.*/connections').test(path):
      return '/management/users/connections';
    case new RegExp('/management/notation/.*/.*/details').test(path):
      return '/management/notation/details';
    case new RegExp('/dashboard/beacon/.*/.*/details').test(path):
      return '/dashboard/beacon/notation/details';
    case path === '/':
      return 'BeaconHome';
    default:
      return path;
  }
}

const FeedbackDialog = (props: IFeedbackDialog) => {
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [canBeContacted, toggleCanBeContacted] = useToggle();
  const [currentStep, setCurrentStep] = useState(Step.FeedbackStep);

  const handleFeedbackSubmit = () => {
    setIsSubmitting(true);

    FeatureFeedbackService.createFeatureFeedback({
      feature: getPathName(location.pathname),
      rating: feedbackRating,
      text: feedbackText,
      usedToOptIn: false,
      usedToOptOut: false,
      canBeContacted: canBeContacted,
    }).finally(() => {
      setIsSubmitting(false);
      setCurrentStep(Step.FeedbackConfimration);
    });
  };

  const handleOK = () => {
    props.close();

    // Reset the page state after the close animation plays out
    setTimeout(() => {
      setFeedbackRating('');
      setFeedbackText('');
      setCurrentStep(Step.FeedbackStep);
      toggleCanBeContacted(false);
    }, 500);
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.close}
      aria-labelledby="feedback-dialog-title"
      disableEscapeKeyDown={
        isSubmitting || currentStep === Step.FeedbackConfimration
      }
      keepMounted
    >
      <DialogTitle id="feedback-dialog-title">
        {currentStep === Step.FeedbackStep
          ? 'Send us your feedback!'
          : 'Thank you for your feedback'}
      </DialogTitle>
      <DialogContent>
        {currentStep === Step.FeedbackStep ? (
          <>
            <Typography
              id="feedback-dialog-rating-label"
              style={{ marginBottom: '5px' }}
            >
              How was your experience using this page?
            </Typography>
            <ButtonGroup
              color="primary"
              aria-labelledby="feedback-dialog-rating-label"
              fullWidth
            >
              <Button
                aria-label="Positive"
                onClick={() => setFeedbackRating('positive')}
                style={{
                  backgroundColor:
                    feedbackRating === 'positive' ? '#9C9C9C' : undefined,
                }}
              >
                <ThumbUpIcon style={{ color: '#000000' }} />
              </Button>
              <Button
                aria-label="Negative"
                onClick={() => setFeedbackRating('negative')}
                style={{
                  backgroundColor:
                    feedbackRating === 'negative' ? '#9C9C9C' : undefined,
                }}
              >
                <ThumbDownIcon style={{ color: '#000000' }} />
              </Button>
            </ButtonGroup>
            <FormHelperText style={{ marginBottom: '15px' }}>
              Required
            </FormHelperText>

            <Typography
              id="feedback-dialog-textarea-label"
              style={{ marginBottom: '5px' }}
            >
              Please describe your experience
            </Typography>
            <TextField
              id="feedback-dialog-textarea"
              variant="outlined"
              multiline
              rows={4}
              fullWidth
              value={feedbackText}
              inputProps={{
                'aria-labelledby': 'feedback-dialog-textarea-label',
              }}
              onChange={e => {
                setFeedbackText(e.target.value);
              }}
            />
            <FormHelperText>Required</FormHelperText>
            <FormControlLabel
              control={
                <Checkbox
                  checked={canBeContacted}
                  onChange={() => toggleCanBeContacted()}
                  name="canBeContacted"
                  color="primary"
                />
              }
              label="I'd like to share more. Feel free to contact me."
            />
          </>
        ) : (
          <Fade in={currentStep === Step.FeedbackConfimration}>
            <Typography>
              Feedback on this page is used to help us understand what
              improvements should be made. Please contact support if you have an
              issue that requires prompt attention.
            </Typography>
          </Fade>
        )}
      </DialogContent>
      <DialogActions>
        <DialogActions>
          {currentStep === Step.FeedbackStep ? (
            <>
              <Button variant="text" color="secondary" onClick={props.close}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackRating || !feedbackText}
              >
                Submit
              </Button>
            </>
          ) : (
            <Button variant="contained" color="primary" onClick={handleOK}>
              Ok
            </Button>
          )}
        </DialogActions>
      </DialogActions>
      {isSubmitting && <LinearProgress />}
    </Dialog>
  );
};

export default FeedbackDialog;
